import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, withRouter } from 'react-router-dom'

import { getAllEventsOpening } from '_redux/modules/event'
import { getMyOrders } from '_redux/modules/order'
import { getMyGroups } from '_redux/modules/group'

import { EVENT_STATUS_OPEN, DESKTOP } from '_utils/constant'
import { convertToCurrentGMT, isDisableEvent } from '_utils/function'
import { getNavTabsQty, getUserInfo, setNavTabsQty } from '_utils/localData'

import { SelectIdGroupContext } from '_context/SelectGroupContext'
import { CollapseContext } from '_context/CollapseMenuContext'
import { DeviceUserContext } from '_context/DeviceUserContext'
import './style.scss'

const countOpenEvents = (allEvents) =>
  allEvents?.filter((item) => item.status === EVENT_STATUS_OPEN)?.length || 0

const countUnclosedPurchases = (myOrders) =>
  myOrders.filter((item) => {
    const isDisable = isDisableEvent(
      item?.status,
      item?.buyerStatus,
      convertToCurrentGMT(item?.ePickupTime),
      item?.ePickupDuration
    )
    return !isDisable
  }).length

export const NavTabs = withRouter(({ location }) => {
  const dispatch = useDispatch()
  const [pathname, setPathname] = useState(location.pathname.split('/'))
  const { allEvents } = useSelector((state) => state.event)
  const { myOrders } = useSelector((state) => state.order)
  const { myGroups } = useSelector((state) => state.group)

  // Show the last known qty instantly (from localStorage) while fresh data loads in the background
  const cachedQty = useRef(getNavTabsQty())
  const [totalEvents, setTotalEvents] = useState(cachedQty.current?.totalEvents || 0)
  const [totalMyPurchases, setTotalMyPurchases] = useState(cachedQty.current?.totalMyPurchases || 0)
  const [totalMyGroups, setTotalMyGroups] = useState(cachedQty.current?.totalMyGroups || 0)

  // Redux slices reset to their initial value on every page load, so the first effect run
  // after mount doesn't represent real data yet - skip it and only react to actual updates.
  const hasSkippedInitialRun = useRef({ events: false, orders: false, groups: false })

  useEffect(() => {
    if (!getNavTabsQty()) {
      dispatch(getAllEventsOpening)
      dispatch(getMyOrders)
      dispatch(getMyGroups)
    }
  }, [dispatch])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.events) {
      hasSkippedInitialRun.current.events = true
      return
    }
    const total = countOpenEvents(allEvents)
    setTotalEvents(total)
    setNavTabsQty({ totalEvents: total })
  }, [allEvents])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.orders) {
      hasSkippedInitialRun.current.orders = true
      return
    }
    const total = countUnclosedPurchases(myOrders)
    setTotalMyPurchases(total)
    setNavTabsQty({ totalMyPurchases: total })
  }, [myOrders])

  useEffect(() => {
    if (!hasSkippedInitialRun.current.groups) {
      hasSkippedInitialRun.current.groups = true
      return
    }
    if (myGroups === null) return
    const total = myGroups.length
    setTotalMyGroups(total)
    setNavTabsQty({ totalMyGroups: total })
  }, [myGroups])

  useEffect(() => {
    setPathname(location.pathname.split('/'))
  }, [location])

  return (
    <div id='nav-tabs-wrap' className='nav-tabs-container'>
      <div className='nav-tabs'>
        <Link
          to='/all-page'
          className={`nav-tab ${pathname[1] === 'all-page' || pathname[1] === '' ? 'active' : ''}`}
        >
          Events  {!!totalEvents && <span id='totalEvents'>&nbsp;({totalEvents})</span>}
        </Link>
        <Link
          to='/list-my-purchases'
          className={`nav-tab ${pathname[1] === 'list-my-purchases' ? 'active' : ''}`}
        >
          My Purchase {!!totalMyPurchases && <span id='totalMyPurchases'>&nbsp;({totalMyPurchases})</span>}
        </Link>
        <Link
          to='/list-my-groups'
          className={`nav-tab ${pathname[1] === 'list-my-groups' ? 'active' : ''}`}
        >
          My Group {!!totalMyGroups && <span id='totalMyGroups'>&nbsp;({totalMyGroups})</span>}
        </Link>
      </div>
    </div>
  )
})

export default function Header() {
  const { infoGroupSelected } = useContext(SelectIdGroupContext)
  const { isCollapse, toggleCollapse } = useContext(CollapseContext)
  const { checkDeviceUser } = useContext(DeviceUserContext)
  const userInfo = getUserInfo()

  return (
    <header className='header-desktop-wrapper'>
      <div className='topbar-teal'>
        <div className='topbar-inner'>
          {userInfo?.isUsingFeature && checkDeviceUser === DESKTOP && (
            <div
              onClick={toggleCollapse}
              className='toggle-drawer-btn'
              title={isCollapse ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 24 24'
                fill='none'
                stroke='white'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <line x1='3' y1='6' x2='21' y2='6' />
                <line x1='3' y1='12' x2='21' y2='12' />
                <line x1='3' y1='18' x2='21' y2='18' />
              </svg>
            </div>
          )}

          {userInfo?.groups?.length > 0 ? (
            <div className={`topbar-brand text-center ${isCollapse ? 'is-collapse' : ''}`}>
              <div className='ttl-grp'>
                {infoGroupSelected?.groupName}
              </div>
              <div className='topbar-logo'>
                co<span className='dot'>•</span>bee
              </div>
            </div>
          ) : (
            <div className={`topbar-brand text-center ${isCollapse ? 'is-collapse' : ''}`}>
              <Link to='/home' style={{ textDecoration: 'none' }}>
                <div className='topbar-logo'>
                  co<span className='dot'>•</span>bee
                </div>
                <div className='topbar-tagline'>Good things must share!</div>
              </Link>
            </div>
          )}

          <Link to='/user/profile' className='avatar-top'>
            <div className='avatar-top__email' aria-hidden='true'>
              <span className='header-notification-icon'>
                <svg viewBox='0 0 24 24' fill='none'>
                  <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
                  <path d='M13.73 21a2 2 0 0 1-3.46 0' />
                </svg>
                <span id='totalNotififications'></span>
              </span>
            </div>
            <div className='avatar-top__img'>
              <img
                id='userAvatar'
                src={
                  userInfo?.photoUrl
                    ? userInfo.photoUrl
                    : process.env.REACT_APP_AVATAR_URL + userInfo?.name
                }
                alt=''
              />
            </div>
          </Link>
        </div>
      </div>
    </header>
  )
}
